import { isPersistentParam } from "src/lib/queryParams";
import { VisitorContextType } from "src/providers/VisitorContext/types";
import { isValidUrl } from ".";

const AFFILIATE_PARAM = "affiliate";
const BYPASS_PARAM = "target";
const FROM_PARAM = "from";

const NEW_SIGNUP_URL = ["staging", "development"].includes(
  process.env.GATSBY_DEPLOY_ENV ?? "",
)
  ? "https://app.staging.shine.fr/register"
  : "https://app.shine.fr/register";

const handleWebAppRedirection = (
  visitorContext: VisitorContextType,
  {
    to,
    from,
  }: {
    from: Location;
    to?: string;
  },
) => {
  const { affiliateLandingHref, search } = visitorContext;

  const url = new URL(NEW_SIGNUP_URL);

  if (affiliateLandingHref) {
    url.searchParams.set(AFFILIATE_PARAM, affiliateLandingHref);
  }

  if (search) {
    const params = new URLSearchParams(search);
    for (const [key, val] of params.entries()) {
      if (!url.searchParams.has(key) && isPersistentParam(key)) {
        url.searchParams.set(key, val);
      }
    }
  }

  // The `target` GET parameter acts as a bypass for some web-app screens
  if (!!to && isValidUrl(to)) {
    const destinationUrl = new URL(to);

    if (destinationUrl.searchParams.has(BYPASS_PARAM)) {
      const value = destinationUrl.searchParams.get(BYPASS_PARAM);
      url.searchParams.set(BYPASS_PARAM, value!);
    }
  }

  /**
   * In some cases during signup, we need to apply custom logic based on the page
   * the user came from on the marketing website.
   * Shine’s marketing website (shine.fr) and web-app (app.shine.fr) are on different origins.
   * This means that by default only the domain will be present in `document.referrer`.
   * @see https://developer.mozilla.org/en-US/docs/Web/API/Document/referrer
   * We don’t want to weaken the referrer policy for all links.
   * So we are persisting this information in url query params.
   * Another solution could be to set a custom referrer policy using the `referrerpolicy`
   * attribute, only for links directing to the app.shine.fr website.
   */
  if (from) {
    url.searchParams.set(FROM_PARAM, from.pathname);
  } else if (typeof window !== "undefined") {
    url.searchParams.set(FROM_PARAM, window.location.pathname);
  }

  return url.toString();
};

export default handleWebAppRedirection;
